const getTimezoneFallback = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? `Timezone ${tz}` : null;
  } catch (_err) {
    return null;
  }
};

const buildLocationLabel = (name: string | null, lat: number, lng: number) => {
  const coords = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  if (name) {
    return `${name} · ${coords}`;
  }
  return `Lat ${lat.toFixed(4)}, Lng ${lng.toFixed(4)}`;
};

const reverseGeocode = async (
  lat: number,
  lng: number,
  timeoutMs = 2500
): Promise<string | null> => {
  if (typeof fetch === "undefined") {
    return null;
  }
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`,
      { signal: controller.signal }
    );
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const city = data.city || data.locality || data.principalSubdivision || "";
    const region = data.principalSubdivision || data.region || "";
    const country = data.countryName || data.countryCode || "";
    const parts = [city, region, country]
      .map((part: string) => String(part || "").trim())
      .filter(Boolean);
    if (parts.length === 0) {
      return null;
    }
    const unique = Array.from(new Set(parts));
    return unique.join(", ");
  } catch (_err) {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
};

export const resolveBrowserLocationLabel = async (
  timeoutMs = 8000
): Promise<string | null> => {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return getTimezoneFallback();
  }

  return new Promise((resolve) => {
    let settled = false;
    const fallback = getTimezoneFallback();
    const finalize = (value: string | null) => {
      if (settled) return;
      settled = true;
      resolve(value ?? fallback);
    };

    const timer = window.setTimeout(
      () => finalize(fallback),
      timeoutMs + 500
    );

    navigator.geolocation.getCurrentPosition(
      (position) => {
        window.clearTimeout(timer);
        const { latitude, longitude } = position.coords;
        reverseGeocode(latitude, longitude).then((name) => {
          finalize(buildLocationLabel(name, latitude, longitude));
        });
      },
      () => {
        window.clearTimeout(timer);
        finalize(fallback);
      },
      { timeout: timeoutMs, maximumAge: 60000, enableHighAccuracy: true }
    );
  });
};
